import { useState } from "react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { InvoiceComposer } from "@/components/InvoiceComposer";
import {
  CreditCard,
  ArrowSquareOut,
  CaretRight,
  ImageSquare,
  Warning,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  AcceptDialog,
  QuoteSignoff,
  ReceivePaymentDialog,
} from "@/components/ProjectDealActions";
import { ApprovalTrail } from "@/components/ApprovalTrail";

const pct = (num, total) =>
  total > 0 ? Math.min(100, Math.max(0, (num / total) * 100)) : 0;

const Bar = ({ label, index, value, total, money, color, sub }) => (
  <div data-testid={`deal-bar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-xs text-zinc-400">
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-zinc-800 text-[9px] font-bold text-zinc-400">
          {index}
        </span>
        {label}
      </span>
      <span className="font-mono-data text-sm font-bold text-zinc-100">
        {money(value)}
      </span>
    </div>
    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-zinc-800">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct(value, total)}%` }} />
    </div>
    <div className="mt-1 text-[10px] text-zinc-500">{sub}</div>
  </div>
);

const ActionBtn = ({ label, testId, onClick, primary }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`flex h-10 flex-1 items-center justify-center gap-1.5 rounded-md border text-xs font-semibold uppercase tracking-wider transition-colors duration-150 ${
      primary
        ? "border-indigo-500/60 bg-indigo-500/15 text-indigo-100 hover:bg-indigo-500/30"
        : "border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-indigo-500 hover:text-indigo-200"
    }`}
  >
    {label}
  </button>
);

export const ProjectOverview = ({ p, live, invoices, money, day, patch, navigate, reload }) => {
  const fin = p.finance || {
    ordered: p.accepted_total || p.quoted_total || 0,
    invoiced: live?.invoiced || 0,
    paid: live?.paid || 0,
    due: (p.accepted_total || 0) - (live?.paid || 0),
  };
  const deal = p.deal;
  const packages = deal?.packages || [];
  const orderLines = deal?.order_lines || [];
  const team = p.team || {};
  const completed = (p.deal_status || "in_progress") === "completed";
  const [payOpen, setPayOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState("");
  const [invOpen, setInvOpen] = useState(false);
  const openInvoices = (invoices || []).filter(
    (i) => i.status !== "Void" && i.status !== "Paid" && Number(i.balance) > 0.009,
  );
  const receiveFor = (invId = "") => {
    setPayInvoice(invId);
    setPayOpen(true);
  };
  const [signoff, setSignoff] = useState(null); // {quote, mode}
  const dealQuote =
    (p.quotes || []).find((q) => q.id === deal?.quote_id) ||
    (p.quotes || [])[0] ||
    null;
  const saveTeam = (field, val) =>
    patch({ team: { ...team, [field]: val } }, true);

  const totalMoney = packages.reduce((n, pk) => n + (pk.total || 0), 0) || fin.ordered;

  return (
    <div className="min-w-0 space-y-4" data-testid="project-overview">
      {/* DEAL & FINANCES */}
      <div className="grid min-w-0 grid-cols-1 gap-4">
        <div className="min-w-0 rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid="deal-finances">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard size={16} weight="bold" className="text-indigo-300" />
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                Deal &amp; Finances
              </span>
              <span
                data-testid="deal-status-pill"
                className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                  completed
                    ? "bg-emerald-500/15 text-emerald-300"
                    : "bg-indigo-500/15 text-indigo-300"
                }`}
              >
                {completed ? "Completed" : "In Progress"}
              </span>
            </div>
            <span className="font-mono-data text-lg font-bold text-zinc-100">
              {money(fin.ordered)}
            </span>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Bar index={1} label="Ordered" value={fin.ordered} total={fin.ordered} money={money}
              color="bg-indigo-500" sub={`${money(Math.max(0, fin.ordered - fin.ordered))} to order`} />
            <Bar index={2} label="Invoiced" value={fin.invoiced} total={fin.ordered} money={money}
              color="bg-emerald-500" sub={`${money(Math.max(0, fin.ordered - fin.invoiced))} to invoice`} />
            <Bar index={3} label="Paid & Applied" value={fin.paid} total={fin.ordered} money={money}
              color="bg-emerald-400" sub={`${money(fin.due)} due`} />
          </div>

          {(invoices || []).filter((i) => i.status !== "Void").length > 0 && (
            <div id="deal-open-invoices" className="mt-4 w-full max-w-full overflow-x-auto rounded-md border border-zinc-800" data-testid="deal-invoices">
              <table className="w-full min-w-[520px] text-left text-xs">
                <thead>
                  <tr className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500">
                    <th className="px-3 py-2 font-semibold">Invoice</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                    <th className="px-3 py-2 text-right font-semibold">Amount</th>
                    <th className="px-3 py-2 text-right font-semibold">Balance</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {(invoices || []).filter((i) => i.status !== "Void").map((i) => {
                    const open = i.status !== "Paid" && Number(i.balance) > 0.009;
                    return (
                      <tr key={i.id} className="border-t border-zinc-800/70" data-testid={`deal-invoice-row-${i.id}`}>
                        <td className="px-3 py-2">
                          <Link to={`/invoices/${i.id}`} className="font-mono-data font-semibold text-indigo-300 hover:underline" data-testid={`deal-invoice-link-${i.id}`}>
                            {i.code}
                          </Link>
                          <span className="ml-2 text-zinc-400">{i.title}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                            i.status === "Paid" ? "bg-emerald-500/15 text-emerald-300"
                              : i.status === "Overdue" ? "bg-red-500/15 text-red-300"
                                : "bg-amber-500/15 text-amber-300"}`}>
                            {i.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono-data text-zinc-200">{money(i.amount)}</td>
                        <td className="px-3 py-2 text-right font-mono-data font-bold text-zinc-100">{money(i.balance)}</td>
                        <td className="px-3 py-2 text-right">
                          {open && (
                            <button
                              type="button"
                              data-testid={`deal-invoice-receive-${i.id}`}
                              onClick={() => receiveFor(i.id)}
                              className="rounded-md border border-zinc-700 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-200 hover:border-indigo-500 hover:text-indigo-200"
                            >
                              Receive
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <label
            data-testid="project-collect-payment-toggle"
            className="mt-4 flex cursor-pointer items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2.5"
          >
            <span className="text-xs">
              <span className="block font-semibold text-zinc-200">
                Collect payment from customer
              </span>
              <span className="block text-[11px] text-zinc-500">
                Show the deposit status and bills to installers in the mobile app
              </span>
            </span>
            <Switch
              data-testid="project-collect-payment-input"
              checked={!!p.collect_payment}
              onCheckedChange={(v) => patch({ collect_payment: v })}
            />
          </label>

          {dealQuote && (
            <div
              className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2"
              data-testid="deal-signoff"
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Sign-off · {dealQuote.code}
              </span>
              <QuoteSignoff
                quote={dealQuote}
                day={day}
                onAccept={(q) => setSignoff({ quote: q, mode: "accept" })}
                onApprove={(q) => setSignoff({ quote: q, mode: "drawing" })}
              />
            </div>
          )}
          <ReceivePaymentDialog
            open={payOpen}
            onClose={() => setPayOpen(false)}
            project={p}
            invoices={invoices}
            initialInvoiceId={payInvoice}
            onDone={() => reload?.()}
          />
          <InvoiceComposer
            open={invOpen}
            onClose={() => setInvOpen(false)}
            project={p}
            onDone={() => reload?.()}
          />
          <AcceptDialog
            open={!!signoff}
            onClose={() => setSignoff(null)}
            project={p}
            quote={signoff?.quote || dealQuote}
            mode={signoff?.mode || "accept"}
            onDone={() => reload?.()}
          />
        </div>
      </div>

      {/* APPROVAL TRAIL — every quote on this project */}
      {(p.quotes || []).some((q) => q.acceptance || (q.drawing_approval || {}).status === "approved") && (
        <div className="space-y-3" data-testid="project-approval-trail">
          {(p.quotes || [])
            .filter((q) => q.acceptance || (q.drawing_approval || {}).status === "approved")
            .map((q) => (
              <div key={q.id}>
                <div className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  <button
                    type="button"
                    data-testid={`project-approval-quote-${q.id}`}
                    onClick={() => navigate(`/quotes/${q.id}`)}
                    className="font-mono-data text-indigo-300 hover:underline"
                  >
                    {q.code}
                  </button>
                  {q.project_name && <span className="normal-case tracking-normal text-zinc-500">{q.project_name}</span>}
                </div>
                <ApprovalTrail quote={q} compact />
              </div>
            ))}
        </div>
      )}

      {/* ORDER line items */}
      {orderLines.length > 0 && (
        <div className="min-w-0 rounded-lg border border-zinc-800 bg-zinc-900" data-testid="project-order">
          <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">Order</span>
            <div className="flex items-center gap-2">
            {deal?.quote_id ? (
              <button
                type="button"
                data-testid="order-open-quote"
                onClick={() => navigate(`/quotes/${deal.quote_id}`)}
                className="flex h-8 items-center gap-1.5 rounded-md bg-indigo-500 px-3 text-[10px] font-bold uppercase tracking-wider text-white transition-colors duration-150 hover:bg-indigo-400"
              >
                <ArrowSquareOut size={13} weight="bold" /> Open Quote
              </button>
            ) : null}
            {deal?.job_id ? (
              <button
                type="button"
                data-testid="order-open-drawing"
                onClick={() => navigate(`/quotes/${deal.quote_id}/design?tab=draw`)}
                className="flex h-8 items-center gap-1.5 rounded-md border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-wider text-zinc-300 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
              >
                <ArrowSquareOut size={13} weight="bold" /> Open Drawing
              </button>
            ) : null}
            </div>
          </div>
          <div className="w-full max-w-full overflow-x-auto">
            <table className="w-full min-w-[860px] table-fixed text-left text-xs [&_td]:h-10 [&_td]:align-middle [&_th]:h-9 [&_th]:align-middle">
              <colgroup>
                <col className="w-[28%]" />
                <col className="w-[30%]" />
                <col className="w-[6%]" />
                <col className="w-[10%]" />
                <col className="w-[6%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-zinc-800 bg-zinc-950/40 text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="px-4 font-semibold">Package / Item</th>
                  <th className="px-3 font-semibold">Description</th>
                  <th className="px-3 text-center font-semibold">Img.</th>
                  <th className="px-3 text-right font-semibold">Quantity</th>
                  <th className="px-3 text-center font-semibold">Inv.</th>
                  <th className="px-3 text-right font-semibold">Unit Price</th>
                  <th className="px-4 text-right font-semibold">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {orderLines.map((ln, i) =>
                  ln.is_group ? (
                    <tr key={i} className="border-b border-zinc-800 bg-zinc-950/60" data-testid={`order-group-${i}`}>
                      <td className="truncate px-4 font-bold text-zinc-100">
                        <span className="mr-2 text-zinc-600">⋮⋮</span>
                        {ln.group}
                        <span className="ml-2 text-[10px] font-normal text-zinc-500">
                          | {ln.item_count} item{ln.item_count === 1 ? "" : "s"}
                        </span>
                      </td>
                      <td colSpan={2} />
                      <td className="px-3 text-right font-mono-data font-semibold text-zinc-200">1x</td>
                      <td colSpan={2} />
                      <td className="px-4 text-right font-mono-data font-bold text-zinc-100">
                        {money(ln.subtotal)}
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} className="border-b border-zinc-800/60 hover:bg-zinc-800/30" data-testid={`order-line-${i}`}>
                      <td className="truncate px-4 pl-6 text-zinc-200">{ln.item}</td>
                      <td className="truncate px-3 text-zinc-400">{ln.desc || ""}</td>
                      <td className="px-3 text-center text-zinc-600">
                        {ln.is_material ? <ImageSquare size={14} className="inline" /> : ""}
                      </td>
                      <td className="px-3 text-right font-mono-data text-zinc-300">
                        {ln.qty} {ln.unit}
                      </td>
                      <td className="px-3 text-center">
                        {ln.is_material ? (
                          <Warning size={14} weight="fill" className="inline text-amber-400" title="Not allocated from stock" />
                        ) : (
                          <span className="text-zinc-600">-</span>
                        )}
                      </td>
                      <td className="px-3 text-right font-mono-data text-zinc-300">
                        {ln.unit_price ? money(ln.unit_price) : "$0.00"}
                      </td>
                      <td className="px-4 text-right font-mono-data text-zinc-100">
                        {money(ln.subtotal)}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-zinc-800 text-zinc-400">
                  <td className="px-4 text-zinc-300">
                    Total: {orderLines.filter((l) => l.is_group).length || 1} item
                    {orderLines.filter((l) => l.is_group).length === 1 ? "" : "s"}
                  </td>
                  <td colSpan={4} />
                  <td className="px-3 text-right text-[11px] uppercase tracking-wider">Subtotal:</td>
                  <td className="px-4 text-right font-mono-data text-zinc-200">
                    {money((deal?.total || totalMoney) - (deal?.tax || 0))}
                  </td>
                </tr>
                <tr className="text-zinc-400">
                  <td colSpan={5} />
                  <td className="px-3 text-right text-[11px] uppercase tracking-wider">Tax:</td>
                  <td className="px-4 text-right font-mono-data">{money(deal?.tax || 0)}</td>
                </tr>
                <tr>
                  <td colSpan={5} />
                  <td className="px-3 text-right text-sm font-bold text-zinc-200">Total:</td>
                  <td className="px-4 text-right font-mono-data text-base font-bold text-zinc-100">
                    {money(deal?.total || totalMoney)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* INVOICES / documents */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid="project-documents">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">
            Financial Documents
          </span>
          <button
            type="button"
            data-testid="documents-all"
            onClick={() => navigate("/invoices")}
            className="flex h-8 items-center gap-1 rounded-md border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-wider text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-200"
          >
            All invoices <CaretRight size={11} weight="bold" />
          </button>
        </div>
        {invoices.length === 0 ? (
          <p className="text-xs text-zinc-500">
            No invoices yet — accepting a quote raises the deposit and balance invoices automatically.
          </p>
        ) : (
          <div className="space-y-2">
            {invoices.map((i) => (
              <Link
                key={i.id}
                to={`/invoices/${i.id}`}
                data-testid={`document-${i.id}`}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3 transition-colors duration-150 hover:border-indigo-500/50"
              >
                <span className="font-mono-data text-xs text-indigo-300">{i.code}</span>
                <span className="min-w-0 flex-1 truncate text-sm">{i.title}</span>
                <span className="text-xs text-zinc-500">
                  {i.due_date ? `Due ${day(i.due_date)}` : "Due on install"}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                  {i.status}
                </span>
                <span className="font-mono-data text-sm font-bold">{money(i.balance)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};
